<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-receipt-text-check-outline"></span></div>
      <div>
        <div class="id-h1">{{ $t('accountingSync.title') }}</div>
        <div class="pg-sub">{{ $t('accountingSync.subtitle') }}</div>
      </div>
    </div>

    <!-- เริ่มงาน + โหมด -->
    <div class="scard acc-start">
      <div class="acc-row">
        <span class="lbl">{{ $t('accountingSync.pickDate') }}</span>
        <id-date class="acc-date" v-model="bizdate"></id-date>
        <button class="id-btn id-btn-primary" :disabled="!bizdate || bizdate > todayTH() || busy" @click="startRun">
          <span class="mdi mdi-play-circle-outline"></span> {{ $t('accountingSync.start') }}
        </button>
        <div class="grid-spacer"></div>
        <span class="lbl">{{ $t('accountingSync.modeLabel') }}</span>
        <id-select class="acc-mode" :model-value="pendingMode || mode" :options="modeOptions" :disabled="busy"
          @update:model-value="askMode"></id-select>
      </div>
      <div v-if="pendingMode" class="acc-confirm-bar">
        <span class="mdi mdi-alert-outline"></span>
        <span>{{ pendingMode === 'approve' ? $t('accountingSync.modeApproveWarn') : $t('accountingSync.modeDraftNote') }}</span>
        <div class="grid-spacer"></div>
        <button class="id-btn id-btn-primary" :disabled="busy" @click="saveMode(pendingMode)">{{ $t('btn.confirm') }}</button>
        <button class="id-btn id-btn-ghost" @click="pendingMode = null">{{ $t('btn.cancel') }}</button>
      </div>
    </div>

    <!-- ผู้ติดต่อที่ระบบเสนอ — ผู้จัดการยืนยัน/ปฏิเสธทีละหลายชื่อ -->
    <template v-if="pending.length">
      <div class="modal-sec acc-sec">
        <span class="mdi mdi-account-question-outline"></span> {{ $t('accountingSync.custTitle') }}
        <span class="badge badge-warning">{{ pending.length }}</span>
        <span class="t-cap">{{ $t('accountingSync.custConfirmedCount', { n: confirmedCount }) }}</span>
      </div>
      <div class="t-cap acc-note">{{ $t('accountingSync.custNote') }}</div>
      <id-data-grid :columns="custColumns" :rows="pending" :per-page="20"
        :search-keys="['sheet_name', 'peak_contact', 'peak_contact_name', 'evidence']">
        <template #cell-pick="{ row }">
          <label class="id-cbx" :aria-label="row.sheet_name" @click="toggleCust(row.sheet_name)">
            <span class="id-check" :class="{ on: custPicked.includes(row.sheet_name) }"><span class="mdi mdi-check"></span></span>
          </label>
        </template>
        <template #cell-peak_contact="{ row }">
          {{ row.peak_contact }} <span class="t-cap">{{ row.peak_contact_name || '' }}</span>
          <span v-if="row.source === 'conflict'" class="badge badge-error acc-proj">{{ $t('accountingSync.custConflict') }}</span>
        </template>
        <template #cell-evidence="{ row }"><span class="t-cap">{{ row.evidence || '' }}</span></template>
      </id-data-grid>
      <div class="acc-confirm">
        <template v-if="!custAction">
          <button class="id-btn id-btn-ghost id-btn-sm" @click="custPicked = custAllPicked ? [] : pending.map((p) => p.sheet_name)">
            {{ custAllPicked ? $t('accountingSync.pickNone') : $t('accountingSync.pickAll') }}
          </button>
          <button class="id-btn id-btn-primary" :disabled="!custConfirmable.length || busy" @click="custAction = 'confirm'">
            {{ $t('accountingSync.custConfirmN', { n: custConfirmable.length }) }}
          </button>
          <button class="id-btn id-btn-ghost" :disabled="!custPicked.length || busy" @click="custAction = 'reject'">
            {{ $t('accountingSync.custRejectN', { n: custPicked.length }) }}
          </button>
        </template>
        <div v-else class="acc-confirm-bar">
          <span class="mdi mdi-alert-outline"></span>
          <span>{{ custAction === 'confirm' ? $t('accountingSync.custConfirmText', { n: custConfirmable.length }) : $t('accountingSync.custRejectText', { n: custPicked.length }) }}</span>
          <div class="grid-spacer"></div>
          <button class="id-btn id-btn-primary" :disabled="busy" @click="decideCustomers">{{ $t('btn.confirm') }}</button>
          <button class="id-btn id-btn-ghost" @click="custAction = null">{{ $t('btn.cancel') }}</button>
        </div>
      </div>
    </template>

    <!-- สินค้าที่ผู้จัดการระบุเอง (once = ใบนั้น · rule = จำข้อความ) — ลบได้ถ้าเลือกผิด -->
    <template v-if="fixes.length">
      <div class="modal-sec acc-sec">
        <span class="mdi mdi-tag-check-outline"></span> {{ $t('accountingSync.fixTitle') }}
        <span class="badge badge-neutral">{{ fixes.length }}</span>
      </div>
      <div class="t-cap acc-note">{{ $t('accountingSync.fixNote') }}</div>
      <id-data-grid :columns="fixColumns" :rows="fixes" :per-page="10" :search-keys="['sheet_text', 'code', 'name', 'customer']">
        <template #cell-kind="{ row }">
          <span class="badge" :class="row.kind === 'rule' ? 'badge-info' : 'badge-neutral'">
            {{ row.kind === 'rule' ? $t('accountingSync.fixKindRule') : $t('accountingSync.fixKindOnce') }}</span>
          <span v-if="row.kind === 'once'" class="t-cap"> {{ $formatShortDate(row.bizdate) }} {{ row.customer }}</span>
        </template>
        <template #cell-code="{ row }">
          <template v-if="row.lines">
            <div v-for="ln in row.lines" :key="ln.code">{{ ln.code }} <span class="t-cap">{{ ln.name || '' }}</span></div>
          </template>
          <template v-else>{{ row.code }} <span class="t-cap">{{ row.name || '' }}</span></template>
        </template>
        <template #cell-price="{ row }">
          <template v-if="row.lines"><div v-for="ln in row.lines" :key="ln.code">{{ qty(ln.qty) }} × {{ $money(ln.price) }}</div></template>
          <template v-else>{{ row.qty != null ? qty(row.qty) + ' × ' : '' }}{{ $money(row.price) }}</template>
        </template>
        <template #cell-del="{ row }">
          <span v-if="fixDeleting === row.fixid" class="acc-contact-btns">
            <button class="id-btn id-btn-primary id-btn-sm" :disabled="busy" @click="deleteFix(row)">{{ $t('btn.confirm') }}</button>
            <button class="id-btn id-btn-ghost id-btn-sm" @click="fixDeleting = null">{{ $t('btn.cancel') }}</button>
          </span>
          <button v-else class="id-btn id-btn-ghost id-btn-sm" v-hint="$t('accountingSync.fixDeleteHint')"
            :aria-label="$t('accountingSync.fixDeleteHint')" @click="fixDeleting = row.fixid"><span class="mdi mdi-delete-outline"></span></button>
        </template>
      </id-data-grid>
    </template>

    <!-- งานล่าสุด -->
    <div class="modal-sec acc-sec"><span class="mdi mdi-history"></span> {{ $t('accountingSync.recentRuns') }}</div>
    <id-data-grid :columns="runColumns" :rows="runs" :loading="loadingRuns" :per-page="10" :search-keys="['bizdate']"
      @row-dblclick="r => openRun(r.runid)">
      <template #cell-bizdate="{ row }">{{ $formatShortDate(row.bizdate) }}</template>
      <template #cell-status="{ row }"><span class="badge" :class="statusBadge(row.status)">{{ statusLabel(row.status) }}</span></template>
      <template #cell-mode="{ row }">{{ modeWord(row.mode) }}</template>
      <template #cell-result="{ row }"><span class="t-cap">{{ countsText(row.counts) }}</span></template>
      <template #cell-view="{ row }">
        <button class="id-btn id-btn-ghost id-btn-sm" v-hint="$t('accountingSync.openRun')" :aria-label="$t('accountingSync.openRun')"
          @click="openRun(row.runid)"><span class="mdi mdi-eye-outline"></span></button>
      </template>
    </id-data-grid>

    <!-- รายละเอียดงาน -->
    <template v-if="selected">
      <div class="modal-sec acc-sec">
        <span class="mdi mdi-receipt-text-outline"></span>
        {{ $t('accountingSync.runTitle', { id: selected.run.runid, date: $formatShortDate(selected.run.bizdate) }) }}
        <span class="badge" :class="statusBadge(selected.run.status)">{{ statusLabel(selected.run.status) }}</span>
        <span v-if="working" class="mdi mdi-loading mdi-spin"></span>
      </div>
      <div class="acc-row acc-tools">
        <span v-if="working" class="t-cap">{{ $t('accountingSync.workingNote') }}</span>
        <span v-if="selected.run.sheetDayTotal" class="t-cap">{{ $t('accountingSync.sheetTotal') }} {{ Number.isFinite(Number(selected.run.sheetDayTotal)) ? $baht(selected.run.sheetDayTotal) : $t('accountingSync.sheetTotalUnreadable') }}</span>
        <div class="grid-spacer"></div>
        <button v-if="selected.run.reportMd" class="id-btn id-btn-ghost id-btn-sm" @click="downloadReport">
          <span class="mdi mdi-download"></span> {{ $t('accountingSync.downloadReport') }}
        </button>
        <button v-if="awaiting && !confirming" class="id-btn id-btn-sm" :class="fixedCount ? 'id-btn-primary' : 'id-btn-ghost'"
          :disabled="busy" @click="replanAsk = true">
          <span class="mdi mdi-refresh"></span> {{ $t('accountingSync.replan') }}
        </button>
        <button v-if="canCancel" class="id-btn id-btn-ghost id-btn-sm" :disabled="busy" @click="cancelRun">{{ $t('accountingSync.cancelRun') }}</button>
      </div>
      <div v-if="awaiting && fixedCount && !replanAsk" class="acc-confirm-bar">
        <span class="mdi mdi-information-outline"></span><span>{{ pendingProductCount ? $t('accountingSync.replanAutoHint', { n: fixedCount, left: pendingProductCount })
          : $t('accountingSync.replanHint', { n: fixedCount }) }}</span>
      </div>
      <div v-if="replanAsk && awaiting" class="acc-confirm-bar">
        <span class="mdi mdi-alert-outline"></span>
        <span>{{ $t('accountingSync.replanText') }}</span>
        <div class="grid-spacer"></div>
        <button class="id-btn id-btn-primary" :disabled="busy" @click="replan">{{ $t('btn.confirm') }}</button>
        <button class="id-btn id-btn-ghost" @click="replanAsk = false">{{ $t('btn.cancel') }}</button>
      </div>
      <div v-if="selected.run.error" class="acc-error">{{ selected.run.error }}</div>

      <id-data-grid v-if="selected.items.length || !working" :columns="itemColumns" :rows="sortedItems" :per-page="50" :search-keys="['customer', 'peak_code', 'peak_contact']"
        :filters="itemFilters">
        <template #cell-pick="{ row }">
          <label v-if="awaiting && row.status === 'CREATE'" class="id-cbx" :aria-label="row.customer" @click="togglePick(row.itemid)">
            <span class="id-check" :class="{ on: picked.includes(row.itemid) }"><span class="mdi mdi-check"></span></span>
          </label>
        </template>
        <template #cell-customer="{ row }">{{ row.customer }} <span class="t-cap">({{ $t('accountingSync.rows') }} {{ row.source_rows.join(', ') }})</span></template>
        <template #cell-lines="{ row }">
          <span class="t-cap">{{ row.lines.map(ln => `${ln.product_code} × ${qty(ln.qty)} @ ${$money(ln.unit_price)}`).join(' · ') }}</span>
          <span v-if="row.project" class="badge badge-warning acc-proj">{{ $t('accountingSync.setProject', { project: row.project }) }}</span>
        </template>
        <template #cell-total="{ row }">{{ $money(row.total) }}</template>
        <template #cell-status="{ row }">
          <span class="badge" :class="itemBadge(row.status)">{{ itemLabel(row.status) }}</span>
          <span v-if="row.peak_code" class="t-cap"> {{ row.peak_code }}</span>
        </template>
        <template #cell-peak_contact="{ row }">
          {{ row.peak_contact || '—' }} <span v-if="row.peak_contact_name" class="t-cap">{{ row.peak_contact_name }}</span>
          <span v-if="row.contact_source === 'name'" class="badge badge-warning acc-proj"
            v-hint="$t('accountingSync.byNameHint')">{{ $t('accountingSync.byName') }}</span>
          <span v-else-if="row.contact_source === 'manual'" class="badge badge-info acc-proj">{{ $t('accountingSync.byManual') }}</span>
          <span v-else-if="row.contact_source === 'new'" class="badge badge-info acc-proj"
            v-hint="$t('accountingSync.byNewHint')">{{ $t('accountingSync.byNew') }}</span>
          <span v-if="contactEditable(row) || lineConfirmable(row)" class="acc-contact-btns">
            <button v-if="contactEditable(row)" class="id-btn id-btn-ghost id-btn-sm" :disabled="busy" @click="openPickContact(row)">
              <span class="mdi mdi-account-search"></span> {{ $t('accountingSync.pickContact') }}
            </button>
            <button v-if="contactEditable(row) && (row.status === 'NO_CONTACT' || row.contact_source === 'new')" class="id-btn id-btn-ghost id-btn-sm" :disabled="busy" @click="openNewContact(row)">
              <span class="mdi mdi-account-plus"></span> {{ $t('accountingSync.newContact') }}
            </button>
            <!-- มีผู้ติดต่อแล้วแต่ยังไม่มีสลิป + มีสลิป LINE ยอดตรงจากผู้ส่งที่ระบบยังไม่รู้จัก → ให้ผู้จัดการดูแล้วยืนยันเอง (16 ต.ค.)
                 รวมแถวที่ได้ผู้ติดต่อจากชื่อ (21 ต.ค.: พริมมี่ ไม่มีปุ่มอะไรให้กดเลย) -->
            <button v-if="lineConfirmable(row)" class="id-btn id-btn-ghost id-btn-sm" :disabled="busy" @click="openLineSlip(row)">
              <span class="mdi mdi-image-search-outline"></span> {{ $t('accountingSync.lineSlipBtn', { n: row.line_candidates.length }) }}
            </button>
          </span>
        </template>
        <template #cell-message="{ row }">
          <span class="t-cap">{{ row.message || '' }}</span>
          <div v-for="iss in row.product_issues" :key="iss.row" class="acc-issue">
            <span class="t-cap">{{ $t('accountingSync.rows') }} {{ iss.row }}: "{{ iss.text }}" {{ $money(iss.total) }}</span>
            <span v-if="iss.fixed" class="badge badge-success acc-proj">
              <span class="mdi mdi-check"></span> {{ fixedText(iss.fixed) }}
              <template v-if="iss.fixed.remember"> · {{ $t('accountingSync.fixKindRule') }}</template>
            </span>
            <button v-if="awaiting && !confirming" class="id-btn id-btn-ghost id-btn-sm" :disabled="busy" @click="openProduct(row, iss)">
              <span class="mdi mdi-tag-search-outline"></span> {{ iss.fixed ? $t('accountingSync.productChange') : $t('accountingSync.pickProduct') }}
            </button>
          </div>
        </template>
      </id-data-grid>

      <div v-if="awaiting" class="acc-confirm">
        <template v-if="!confirming">
          <button class="id-btn id-btn-ghost id-btn-sm" :disabled="!autoPickable.length" @click="pickAll(!allPicked)">
            {{ allPicked ? $t('accountingSync.pickNone') : $t('accountingSync.pickAll') }}
          </button>
          <button class="id-btn id-btn-primary" :disabled="!picked.length || busy" @click="confirming = true">
            {{ $t('accountingSync.createN', { n: picked.length, mode: modeWord(confirmMode) }) }}
          </button>
          <span v-if="!creatableCount" class="t-cap">{{ $t('accountingSync.nothingToCreate') }}</span>
        </template>
        <div v-else class="acc-confirm-bar">
          <span class="mdi mdi-alert-outline"></span>
          <span>{{ $t('accountingSync.confirmText', { n: picked.length, mode: modeWord(confirmMode), date: $formatShortDate(selected.run.bizdate) }) }}</span>
          <div class="grid-spacer"></div>
          <button class="id-btn id-btn-primary" :disabled="busy" @click="confirmRun">{{ $t('btn.confirm') }}</button>
          <button class="id-btn id-btn-ghost" @click="confirming = false">{{ $t('btn.cancel') }}</button>
        </div>
      </div>
    </template>

    <!-- แถวที่ไม่พบผู้ติดต่อ: เลือกผู้ติดต่อ Peak เอง / สั่งสร้างใหม่ (เจ้าของเคาะ 7 ต.ค.) -->
    <id-modal v-model="pickDlg" size="md" icon="mdi-account-search" :title="$t('accountingSync.pickContactTitle')"
      :subtitle="contactRow ? contactRow.customer : ''">
      <input class="id-input" v-model="contactQ" name="peak-contact-q" autocomplete="off"
        :placeholder="$t('accountingSync.pickContactSearch')" @input="searchContacts">
      <div class="t-cap acc-note">{{ $t('accountingSync.pickContactHint') }}</div>
      <div class="acc-contact-list">
        <button v-for="c in contactHits" :key="c.code" class="acc-contact-hit" :disabled="busy" @click="pickContact(c.code)">
          <b>{{ c.code }}</b> <span>{{ c.name }}</span>
        </button>
        <div v-if="contactQ.trim().length >= 2 && !contactHits.length && !contactSearching" class="t-cap">{{ $t('accountingSync.pickContactNone') }}</div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="pickDlg = false">{{ $t('btn.cancel') }}</button>
      </template>
    </id-modal>
    <!-- แถวชีตที่ระบบไม่รู้จักสินค้า/ยอดไม่ลงตัว: ผู้จัดการเลือกสินค้า Peak + จำนวน (เจ้าของเคาะ 15 ต.ค.) -->
    <id-modal v-model="productDlg" size="md" icon="mdi-tag-search-outline" :title="$t('accountingSync.productTitle')"
      :subtitle="productIssue ? `${productRow.customer} · ${$t('accountingSync.rows')} ${productIssue.row}` : ''">
      <template v-if="productIssue">
        <div class="acc-pd-sum">
          <div class="acc-pd-text">"{{ productIssue.text }}"</div>
          <div class="acc-pd-amt">
            <span class="t-cap">{{ $t('accountingSync.productAmount') }}</span>
            <b>{{ $money(productIssue.purchase) }}</b>
          </div>
        </div>
        <!-- "ไม่รู้จักสินค้า" ซ้ำกับหัวกล่องอยู่แล้ว — โชว์ข้อความระบบเฉพาะกรณียอด/จำนวนไม่ตรง -->
        <div v-if="productIssue.code !== 'PRODUCT_UNMAPPED'" class="t-cap acc-note">{{ productIssue.message }}</div>
        <div class="acc-pd-search">
          <span class="mdi mdi-magnify"></span>
          <input class="id-input" v-model="productQ" name="peak-product-q" autocomplete="off" :placeholder="$t('accountingSync.productSearch')">
        </div>
        <!-- เลือกได้หลายสินค้า = แบ่งยอดของแถว (เจ้าของเคาะ 4 พ.ย.: ค่าแข่งที่โอนรวม = ส่วน iStar + ส่วนนำส่งผู้จัดแข่ง) -->
        <div class="t-cap acc-pd-multi">{{ $t('accountingSync.productMultiHint') }}</div>
        <div class="acc-pd-list">
          <template v-for="g in productGroups" :key="g.kind">
            <div class="acc-pd-group">{{ g.label }}</div>
            <button v-for="p in g.items" :key="p.code" type="button" class="acc-pd-row" :class="{ on: isPicked(p.code) }"
              :aria-pressed="isPicked(p.code) ? 'true' : 'false'" @click="toggleProductLine(p.code)">
              <span class="mdi acc-pd-radio" :class="isPicked(p.code) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"></span>
              <span class="acc-pd-name">{{ p.name }}<span class="acc-pd-code">{{ p.code }}</span></span>
              <!-- ราคาจาก Peak เท่านั้น (เจ้าของ 15 ต.ค.: ไม่เอาราคาที่ระบบจำเอง) — Peak ยังไม่ตั้งราคา = ไม่แสดง -->
              <span v-if="p.price != null" class="acc-pd-price">{{ $money(p.price) }}</span>
            </button>
          </template>
          <div v-if="!products.length" class="t-cap acc-pd-empty">{{ $t('accountingSync.productEmpty') }}</div>
          <div v-else-if="!productHits.length" class="t-cap acc-pd-empty">{{ $t('accountingSync.productNone') }}</div>
        </div>
        <!-- สินค้าเดียว = รับยอดทั้งแถว (เหมือนเดิม) · หลายสินค้า = กรอกยอดแต่ละรายการ รวมต้องเท่ายอดสินค้าพอดี -->
        <div v-for="ln in productLines" :key="ln.code" class="acc-pd-line" :class="{ split: productSplit }">
          <div v-if="productSplit" class="acc-pd-line-head">
            <span class="acc-pd-name">{{ productName(ln.code) }}<span class="acc-pd-code">{{ ln.code }}</span></span>
            <button type="button" class="id-btn id-btn-ghost id-btn-sm" v-hint="$t('accountingSync.productLineRemove')"
              :aria-label="$t('accountingSync.productLineRemove')" @click="toggleProductLine(ln.code)"><span class="mdi mdi-close"></span></button>
          </div>
          <div class="acc-pd-calc">
            <div class="field acc-pd-qty">
              <label>{{ $t('accountingSync.productQty') }}</label>
              <input class="id-input" type="number" min="0.5" step="0.5" v-model="ln.qty" :name="`peak-product-qty-${ln.code}`" autocomplete="off">
            </div>
            <div v-if="productSplit" class="field acc-pd-qty">
              <label>{{ $t('accountingSync.productLineAmount') }} <span class="req">*</span></label>
              <id-money v-model="ln.amount" :name="`peak-product-amt-${ln.code}`" autocomplete="off"></id-money>
            </div>
            <div class="acc-pd-unit">
              <template v-if="lineUnit(ln)">{{ $t('accountingSync.productUnit', { price: $money(lineUnit(ln)) }) }}</template>
              <span v-else-if="!productSplit || lineCents(ln) > 0" class="acc-err">{{ $t('accountingSync.productUnitBad') }}</span>
            </div>
          </div>
          <div v-if="linePeakDiff(ln)" class="acc-err acc-note">{{ $t('accountingSync.productPeakDiff', { price: $money(linePeakDiff(ln)) }) }}</div>
        </div>
        <div v-if="productSplit" class="acc-pd-remain" :class="{ ok: !productRemainCents, bad: productRemainCents }">
          <span class="mdi" :class="productRemainCents ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline'"></span>
          <span>{{ $t('accountingSync.productSplitSum', { sum: $money(productSplitCents / 100), total: $money(productIssue.purchase) }) }}</span>
          <b v-if="productRemainCents">{{ $t(productRemainCents > 0 ? 'accountingSync.productSplitLeft' : 'accountingSync.productSplitOver',
            { amount: $money(Math.abs(productRemainCents) / 100) }) }}</b>
        </div>
        <!-- จำได้เฉพาะสินค้าที่ไม่รู้จัก + สินค้าเดียว: แถวที่ระบบรู้จักแล้ว กฎเดิมชนะเสมอ · แบ่งหลายสินค้าไม่จำ (ยอดแต่ละส่วนเปลี่ยนตามรายการแข่ง) -->
        <label v-if="productIssue.code === 'PRODUCT_UNMAPPED' && !productSplit" class="id-cbx acc-pd-remember" @click="productRemember = !productRemember">
          <span class="id-check" :class="{ on: productRemember }"><span class="mdi mdi-check"></span></span>
          <span>{{ $t('accountingSync.productRemember', { text: productIssue.text }) }}</span>
        </label>
      </template>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="productDlg = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="busy || !productValid" @click="saveProduct">{{ $t('btn.confirm') }}</button>
      </template>
    </id-modal>
    <id-modal v-model="lineDlg" size="xl" icon="mdi-image-search-outline" :title="$t('accountingSync.lineSlipTitle')"
      :subtitle="lineRow ? `${lineRow.customer} · ${$money(lineRow.total)}` : ''">
      <div class="t-cap acc-note">{{ $t('accountingSync.lineSlipNote') }}</div>
      <div v-for="c in (lineRow ? lineRow.line_candidates : [])" :key="c.message_id" class="acc-line-slip">
        <a v-if="lineImgs[c.message_id]" :href="lineImgs[c.message_id]" target="_blank" rel="noopener" class="acc-line-imglink"
          v-hint="$t('accountingSync.lineSlipOpenFull')" :aria-label="$t('accountingSync.lineSlipOpenFull')">
          <img :src="lineImgs[c.message_id]" class="acc-line-img" alt="" @error="lineImgs[c.message_id] = null">
        </a>
        <div v-else class="t-cap acc-line-noimg">{{ $t('accountingSync.lineSlipNoImage') }}</div>
        <div class="acc-line-meta">
          <b>{{ $money(c.amount) }}</b>
          <span class="t-cap">{{ $t('accountingSync.lineSlipSent', { at: c.sent_at }) }}</span>
          <button class="id-btn id-btn-primary id-btn-sm" :disabled="busy" @click="confirmLineSlip(c.message_id)">
            <span class="mdi mdi-check"></span> {{ $t('accountingSync.lineSlipUse') }}
          </button>
          <button class="id-btn id-btn-ghost id-btn-sm" :disabled="busy || lineDownloading === c.message_id" @click="downloadLineSlip(c)">
            <span class="mdi mdi-download"></span> {{ $t('accountingSync.lineSlipDownload') }}
          </button>
        </div>
      </div>
      <div class="t-cap acc-note">{{ $t('accountingSync.lineSlipEffect') }}</div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="lineDlg = false">{{ $t('btn.cancel') }}</button>
      </template>
    </id-modal>
    <id-modal v-model="newDlg" size="sm" icon="mdi-account-plus" :title="$t('accountingSync.newContactTitle')">
      <div class="field">
        <label>{{ $t('accountingSync.newContactPhone') }}</label>
        <input class="id-input" v-model="newPhone" name="peak-new-contact-phone" autocomplete="off" inputmode="tel"
          :placeholder="$t('accountingSync.newContactPhonePh')">
      </div>
      <div v-if="contactRow" class="acc-note">{{ $t('accountingSync.newContactText', { name: newContactName }) }}</div>
      <div class="t-cap acc-note">{{ $t('accountingSync.newContactNote') }}</div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="newDlg = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="busy || !phoneOk" @click="setContact('new')">{{ $t('btn.confirm') }}</button>
      </template>
    </id-modal>

    <!-- กระทบยอด statement กสิกร (worker อ่าน statement + PDF ค่าธรรมเนียมจาก Drive · อ่านอย่างเดียว) -->
    <div class="modal-sec acc-sec"><span class="mdi mdi-bank-check"></span> {{ $t('accountingSync.reconTitle') }}</div>
    <div class="t-cap acc-note">{{ $t('accountingSync.reconNote') }}</div>
    <div class="acc-row acc-tools">
      <id-select class="acc-mode" v-model="reconMonth" :options="reconMonthOptions"></id-select>
      <button class="id-btn id-btn-primary" :disabled="!reconMonth || busy" @click="startRecon">
        <span class="mdi mdi-play-circle-outline"></span> {{ $t('accountingSync.reconStart') }}
      </button>
    </div>
    <id-data-grid :columns="reconColumns" :rows="reconRuns" :loading="loadingRecon" :per-page="10" :search-keys="['month']"
      @row-dblclick="r => openRecon(r.reconid)">
      <template #cell-month="{ row }">{{ monthLabel(row.month) }}</template>
      <template #cell-status="{ row }"><span class="badge" :class="statusBadge(row.status)">{{ reconStatusLabel(row.status) }}</span></template>
      <template #cell-result="{ row }">
        <span v-if="row.status === 'done'" class="t-cap">{{ $t('accountingSync.reconResult', { ok: row.ok, bad: row.bad }) }}</span>
        <span v-else-if="row.error" class="t-cap">{{ row.error }}</span>
      </template>
      <template #cell-finishdate="{ row }">{{ row.finishdate ? $formatDateTime(row.finishdate) : '—' }}</template>
      <template #cell-view="{ row }">
        <button class="id-btn id-btn-ghost id-btn-sm" v-hint="$t('accountingSync.openRun')" :aria-label="$t('accountingSync.openRun')"
          @click="openRecon(row.reconid)"><span class="mdi mdi-eye-outline"></span></button>
      </template>
    </id-data-grid>

    <template v-if="recon">
      <div class="modal-sec acc-sec">
        <span class="mdi mdi-file-document-outline"></span> {{ $t('accountingSync.reconRunTitle', { month: monthLabel(recon.month) }) }}
        <span class="badge" :class="statusBadge(recon.status)">{{ reconStatusLabel(recon.status) }}</span>
        <span v-if="reconActive(recon.status)" class="mdi mdi-loading mdi-spin"></span>
        <div class="grid-spacer"></div>
        <button v-if="reconActive(recon.status)" class="id-btn id-btn-ghost id-btn-sm" :disabled="busy" @click="cancelRecon">
          {{ $t('accountingSync.reconCancel') }}
        </button>
        <button v-if="recon.report_md" class="id-btn id-btn-ghost id-btn-sm" @click="downloadRecon">
          <span class="mdi mdi-download"></span> {{ $t('accountingSync.downloadReport') }}
        </button>
      </div>
      <div v-if="reconActive(recon.status)" class="t-cap">{{ $t('accountingSync.reconWorking') }}</div>
      <div v-if="recon.error" class="acc-error">{{ recon.error }}</div>
      <template v-if="recon.summary">
        <div class="acc-recon-totals">
          <div v-for="g in reconGroups" :key="g.key" class="acc-recon-total">
            <div class="acc-recon-head">{{ g.title }}</div>
            <div v-for="(r, i) in g.rows" :key="i" class="acc-recon-line" :class="{ 'acc-recon-sum': r.op === '=' }">
              <span class="acc-recon-op">{{ r.op }}</span><span class="acc-recon-label">{{ r.label }}</span><span>{{ r.value }}</span>
            </div>
            <div class="t-cap acc-recon-verdict" :class="g.ok ? 'acc-good' : 'acc-warn'">{{ g.verdict }}</div>
            <div v-if="g.note" class="t-cap">{{ g.note }}</div>
          </div>
        </div>
        <div class="t-cap acc-note">{{ $t('accountingSync.reconCheckedAt', { at: recon.summary.checked_at, ok: recon.summary.ok, days: recon.summary.days }) }}</div>
        <div v-if="recon.summary.bad.length" class="acc-recon-list">
          <div class="acc-recon-head acc-bad"><span class="mdi mdi-alert-circle-outline"></span> {{ $t('accountingSync.reconBad', { n: recon.summary.bad.length }) }}</div>
          <div v-for="b in recon.summary.bad" :key="'b' + b.day" class="acc-recon-day">
            <b>{{ $formatShortDate(b.day) }}</b>
            <div v-for="(t, i) in b.items" :key="i" class="t-cap">{{ t }}</div>
          </div>
        </div>
        <div v-else class="acc-recon-head acc-good"><span class="mdi mdi-check-circle-outline"></span> {{ $t('accountingSync.reconAllGood') }}</div>
        <div v-if="recon.summary.explained.length" class="acc-recon-list">
          <div class="acc-recon-head"><span class="mdi mdi-information-outline"></span> {{ $t('accountingSync.reconExplained', { n: recon.summary.explained.length }) }}</div>
          <div v-for="b in recon.summary.explained" :key="'e' + b.day" class="acc-recon-day">
            <b>{{ $formatShortDate(b.day) }}</b>
            <div v-for="(t, i) in b.items" :key="i" class="t-cap">{{ t }}</div>
          </div>
        </div>
        <div v-if="recon.summary.notes.length" class="acc-recon-list">
          <div class="acc-recon-head"><span class="mdi mdi-note-text-outline"></span> {{ $t('accountingSync.reconNotes') }}</div>
          <div v-for="(n, i) in recon.summary.notes" :key="i" class="t-cap">{{ n }}</div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { preloadImage } from '@/utils/image';

const ACTIVE = ['queued', 'planning', 'confirmed', 'creating'];
const ITEM_ORDER = ['FAILED', 'CHANGED', 'CREATING', 'CREATE', 'CREATED', 'CONTACT_UNCONFIRMED', 'NO_SLIP', 'NO_CONTACT',
  'CONTACT_AMBIGUOUS', 'POSSIBLE_DUPLICATE', 'HELD', 'GONE', 'IN_PEAK'];
const DETAIL_POLL_MS = 10000; // worker กำลังทำงาน (2–5 นาที) — เร็วกว่าโพลเงียบ 60 วิ เฉพาะช่วงนั้น

export default {
  name: 'AccountingSync',
  emits: ['onErrorHandler', 'onInfoHandler', 'onClickChangeState', 'onLoading'],
  data() {
    return {
      runs: [], mode: 'draft', pendingMode: null, loadingRuns: true,
      bizdate: '', selected: null, picked: [], confirming: false, busy: false,
      listTimer: null, detailTimer: null, alive: true,
      pending: [], confirmedCount: 0, custPicked: [], custAction: null,
      reconRuns: [], loadingRecon: true, reconMonth: '', recon: null, reconTimer: null, reconWant: null,
      pickDlg: false, newDlg: false, newPhone: '', contactRow: null, contactQ: '', contactHits: [], contactSearching: false,
      contactTimer: null, contactSeq: 0, runSeq: 0,
      fixes: [], fixDeleting: null, replanAsk: false,
      productDlg: false, productRow: null, productIssue: null, products: [], productQ: '',
      productLines: [], productRemember: false,
      lineDlg: false, lineRow: null, lineImgs: {}, lineDownloading: null,
    };
  },
  computed: {
    // ตรงกับฝั่ง server: ใบจริงก็ต่อเมื่อทั้งตอนเริ่มงานและตอนนี้ตั้งเป็นใบจริง
    confirmMode() { return this.selected && this.selected.run.mode === 'approve' && this.mode === 'approve' ? 'approve' : 'draft'; },
    custColumns() {
      return [
        { key: 'pick', label: '' },
        { key: 'sheet_name', label: this.$t('accountingSync.colSheetName') },
        { key: 'peak_contact', label: this.$t('accountingSync.colPeakContact') },
        { key: 'evidence', label: this.$t('accountingSync.colEvidence') },
      ];
    },
    custConfirmable() {
      const ok = new Set(this.pending.filter((p) => p.source !== 'conflict').map((p) => p.sheet_name));
      return this.custPicked.filter((n) => ok.has(n));
    },
    reconMonthOptions() {
      // the last 12 months up to the current one (Bangkok), newest first
      const now = new Date(Date.now() + 7 * 3600 * 1000);
      const out = [];
      for (let i = 0; i < 12; i += 1) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
        const v = d.toISOString().slice(0, 7);
        out.push({ value: v, label: this.monthLabel(v) });
      }
      return out;
    },
    reconColumns() {
      return [
        { key: 'month', label: this.$t('accountingSync.reconMonth') },
        { key: 'status', label: this.$t('accountingSync.colStatus') },
        { key: 'result', label: this.$t('accountingSync.colResult') },
        { key: 'finishdate', label: this.$t('accountingSync.reconFinished') },
        { key: 'view', label: '' },
      ];
    },
    // three boxes, each written as the sum it checks (owner, 7 Oct): money in satang so the sums are exact
    reconGroups() {
      const t = (this.recon && this.recon.summary && this.recon.summary.totals) || {};
      const sat = (k) => Math.round(Number(t[k] || 0) * 100);
      const L = (k) => this.$t('accountingSync.reconTotal.' + k);
      const m = (s) => this.$money(s / 100);
      const row = (op, label, s) => ({ op, label, value: m(s) });
      const verdict = (a, b) => (a === b ? this.$t('accountingSync.reconTotal.match')
        : this.$t('accountingSync.reconTotal.diff', { amount: m(Math.abs(a - b)) }));
      const has = (k) => t[k] !== null && t[k] !== undefined; // a key never recorded is not a shortfall
      const cardNet = sat('sheet_credit') - sat('fee') - sat('vat');
      const groups = [];
      if (has('qr_in')) {
        groups.push({ key: 'bank', title: L('groupBank'), ok: sat('sheet_bank') === sat('qr_in'),
          verdict: verdict(sat('sheet_bank'), sat('qr_in')),
          rows: [row('', L('sheet_bank'), sat('sheet_bank')), row('', L('qr_in'), sat('qr_in'))] });
      }
      if (has('card_in')) {
        groups.push({ key: 'card', title: L('groupCard'), ok: cardNet === sat('card_in'), verdict: verdict(cardNet, sat('card_in')),
          note: sat('wht') ? this.$t('accountingSync.reconTotal.whtNote', { amount: m(sat('wht')) }) : '',
          rows: [row('', L('sheet_credit'), sat('sheet_credit')), row('−', L('fee'), sat('fee')),
            row('−', L('vat'), sat('vat')), row('=', L('cardExpected'), cardNet), row('', L('card_in'), sat('card_in'))] });
      }
      if (has('peak')) {
        const sheet = sat('sheet_bank') + sat('sheet_credit') + sat('sheet_cash');
        groups.push({ key: 'peak', title: L('groupPeak'), ok: sheet === sat('peak'),
          verdict: sheet === sat('peak') ? L('match') : this.$t('accountingSync.reconTotal.peakDiff', { amount: m(Math.abs(sheet - sat('peak'))) }),
          rows: [row('', L('sheet_bank'), sat('sheet_bank')), row('+', L('sheet_credit'), sat('sheet_credit')),
            row('+', L('sheet_cash'), sat('sheet_cash')), row('=', L('sheetSum'), sheet), row('', L('peak'), sat('peak'))] });
      }
      return groups;
    },
    custAllPicked() { return this.pending.length > 0 && this.custPicked.length === this.pending.length; },
    modeOptions() {
      return [{ value: 'draft', label: this.$t('accountingSync.modeDraft') },
        { value: 'approve', label: this.$t('accountingSync.modeApprove') }];
    },
    runColumns() {
      return [
        { key: 'runid', label: '#' },
        { key: 'bizdate', label: this.$t('accountingSync.colDate') },
        { key: 'status', label: this.$t('accountingSync.colStatus') },
        { key: 'mode', label: this.$t('accountingSync.colMode') },
        { key: 'result', label: this.$t('accountingSync.colResult') },
        { key: 'view', label: '' },
      ];
    },
    itemColumns() {
      const cols = [
        { key: 'customer', label: this.$t('accountingSync.colCustomer') },
        { key: 'lines', label: this.$t('accountingSync.colLines') },
        { key: 'total', label: this.$t('accountingSync.colTotal'), align: 'right' },
        { key: 'status', label: this.$t('accountingSync.colStatus') },
        { key: 'peak_contact', label: this.$t('accountingSync.colContact') },
        { key: 'message', label: this.$t('accountingSync.colNote') },
      ];
      return this.awaiting ? [{ key: 'pick', label: '' }, ...cols] : cols;
    },
    itemFilters() {
      const seen = [...new Set((this.selected ? this.selected.items : []).map((i) => i.status))];
      return [{ key: 'status', label: this.$t('accountingSync.colStatus'), options: seen.map((s) => ({ value: s, label: this.itemLabel(s) })) }];
    },
    newContactName() {
      const r = this.contactRow;
      return r ? `${String(r.customer).replace(/\s+/g, ' ').trim()} ${this.newPhone.trim()}` : '';
    },
    phoneOk() { // same shapes the server accepts: mobile 10 digits, Bangkok landline 02 + 7, +66…
      const d = this.newPhone.replace(/\D/g, '').replace(/^66(?=\d{8,9}$)/, '0');
      return /^0[1-9]\d{8}$/.test(d) || /^02\d{7}$/.test(d);
    },
    fixColumns() {
      return [
        { key: 'kind', label: this.$t('accountingSync.colWhere') },
        { key: 'sheet_text', label: this.$t('accountingSync.colSheetText') },
        { key: 'code', label: this.$t('accountingSync.colProduct') },
        { key: 'price', label: this.$t('accountingSync.colUnitPrice'), align: 'right' },
        { key: 'del', label: '' },
      ];
    },
    pendingProductCount() {
      return this.selected ? this.selected.items.reduce((n, i) => n + (i.product_issues || []).filter((x) => !x.fixed).length, 0) : 0;
    },
    manualContactCount() {
      return this.selected ? this.selected.items.filter((i) => ['manual', 'new'].includes(i.contact_source)).length : 0;
    },
    fixedCount() {
      return this.selected ? this.selected.items.reduce((n, i) => n + (i.product_issues || []).filter((x) => x.fixed).length, 0) : 0;
    },
    productGroups() {
      return ['product', 'service']
        .map((kind) => ({ kind, label: this.$t(kind === 'product' ? 'accountingSync.productGroupGoods' : 'accountingSync.productGroupServices'),
          items: this.productHits.filter((p) => p.kind === kind) }))
        .filter((g) => g.items.length);
    },
    productHits() {
      const q = this.productQ.trim().toLowerCase();
      return q ? this.products.filter((p) => `${p.code} ${p.name}`.toLowerCase().includes(q)) : this.products;
    },
    productPurchaseCents() { return this.productIssue ? Math.round(Number(this.productIssue.purchase) * 100) : 0; },
    productSplit() { return this.productLines.length > 1; },
    productSplitCents() { return this.productLines.reduce((n, ln) => n + this.lineCents(ln), 0); },
    productRemainCents() { return this.productPurchaseCents - this.productSplitCents; },
    // ทุกบรรทัดต้องหารลงตัว + ตรงราคา Peak และยอดรวมต้องเท่ายอดสินค้าพอดี — กติกาเดียวกับ server
    productValid() {
      return this.productLines.length > 0 && !this.productRemainCents
        && this.productLines.every((ln) => this.lineCents(ln) > 0 && this.lineUnit(ln) && !this.linePeakDiff(ln));
    },
    awaiting() { return !!this.selected && this.selected.run.status === 'awaiting_confirm'; },
    working() { return !!this.selected && ACTIVE.includes(this.selected.run.status); },
    canCancel() { return !!this.selected && ['queued', 'awaiting_confirm', 'confirmed', 'failed'].includes(this.selected.run.status); },
    creatableCount() { return this.selected ? this.selected.items.filter((i) => i.status === 'CREATE').length : 0; },
    // ใบที่รู้ผู้ติดต่อจากชื่ออย่างเดียว ปุ่ม "เลือกทั้งหมด" ไม่แตะ — ผู้จัดการต้องตรวจแล้วติ๊กเองทีละใบ
    autoPickable() { return this.selected ? this.selected.items.filter((i) => i.status === 'CREATE' && i.contact_source !== 'name').map((i) => i.itemid) : []; },
    allPicked() { return this.autoPickable.length > 0 && this.autoPickable.every((id) => this.picked.includes(id)); },
    sortedItems() {
      const rank = (s) => { const i = ITEM_ORDER.indexOf(s); return i < 0 ? 99 : i; };
      return this.selected ? [...this.selected.items].sort((a, b) => rank(a.status) - rank(b.status)) : [];
    },
  },
  mounted() {
    this.loadRuns();
    this.loadCustomers();
    this.loadFixes();
    this.loadRecon();
    this.listTimer = setInterval(() => this.loadRuns(true), 60000); // โพลเงียบมาตรฐานของแอป
  },
  unmounted() {
    this.alive = false; // คำขอที่ค้างอยู่ตอนออกจากหน้า ต้องไม่ตั้งโพลรอบใหม่
    clearInterval(this.listTimer);
    clearTimeout(this.detailTimer);
    clearTimeout(this.reconTimer);
    clearTimeout(this.contactTimer);
  },
  methods: {
    todayTH() { return new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10); },
    auth() { return { headers: { Authorization: `Bearer ${this.$store.getters.getToken}` } }; },
    fail(error) { this.$emit('onErrorHandler', error.response && error.response.data ? error.response.data.message : error.message); },
    qty(q) { return Number(q).toString(); },
    modeWord(m) { return m === 'approve' ? this.$t('accountingSync.modeApproveShort') : this.$t('accountingSync.modeDraftShort'); },
    statusLabel(s) { return this.$t('accountingSync.status.' + s); },
    statusBadge(s) {
      return { done: 'badge-success', failed: 'badge-error', cancelled: 'badge-neutral', awaiting_confirm: 'badge-warning' }[s] || 'badge-info';
    },
    itemLabel(s) {
      const k = 'accountingSync.item.' + s;
      const t = this.$t(k);
      return t === k ? s : t;
    },
    itemBadge(s) {
      return { CREATE: 'badge-primary', CREATED: 'badge-success', IN_PEAK: 'badge-neutral', GONE: 'badge-neutral', FAILED: 'badge-error',
        CHANGED: 'badge-error', CREATING: 'badge-info' }[s] || 'badge-warning';
    },
    countsText(c) {
      if (!c || !Object.keys(c).length) return '';
      return Object.entries(c).map(([k, v]) => `${this.itemLabel(k)} ${v}`).join(' · ');
    },
    async loadRuns(silent = false) {
      try {
        const r = await axios.post(this.baseURL + '/accountingRuns', {}, this.auth());
        this.runs = r.data.runs || [];
        this.mode = r.data.mode || 'draft';
      } catch (error) {
        if (!silent || (error.response && error.response.status === 401)) this.fail(error);
      } finally {
        this.loadingRuns = false;
      }
    },
    async openRun(runid, silent = false) {
      clearTimeout(this.detailTimer);
      // คำขอล่าสุดชนะเสมอ: คำตอบช้าของงานที่เปิดก่อน/โพลรอบเก่า ห้ามทับจอ และห้ามตั้ง timer โพลซ้อน
      const seq = ++this.runSeq;
      try {
        const r = await axios.post(this.baseURL + '/accountingRunDetail', { runid }, this.auth());
        if (seq !== this.runSeq) return;
        const changed = !this.selected || this.selected.run.runid !== runid || this.selected.run.status !== r.data.run.status;
        this.selected = { run: r.data.run, items: r.data.items || [] };
        if (changed) {
          this.picked = this.selected.items.filter((i) => i.status === 'CREATE' && i.selected).map((i) => i.itemid);
          this.confirming = false;
          if (silent) { this.loadRuns(true); this.loadCustomers(true); } // แผนเสร็จ = อาจมีชื่อที่ระบบเสนอใหม่
        }
        if (this.working && this.alive) this.detailTimer = setTimeout(() => this.openRun(runid, true), DETAIL_POLL_MS);
      } catch (error) {
        if (!silent || (error.response && error.response.status === 401)) this.fail(error);
        // คำขอล่าสุดล้ม (เน็ตสะดุด) — ยังโพลงานบนจอต่อ ไม่งั้นจอค้างจนกว่าจะมีคนกดเอง
        if (seq === this.runSeq && this.working && this.alive) {
          this.detailTimer = setTimeout(() => this.openRun(this.selected.run.runid, true), DETAIL_POLL_MS);
        }
      }
    },
    async mutate(url, body) {
      this.busy = true;
      this.$emit('onLoading', true);
      try {
        return await axios.post(this.baseURL + url, body, this.auth());
      } finally {
        this.$emit('onLoading', false);
        this.busy = false;
      }
    },
    async startRun() {
      try {
        const r = await this.mutate('/accountingStartRun', { bizdate: this.bizdate });
        await this.loadRuns(true);
        await this.openRun(r.data.runid);
      } catch (error) {
        const runid = error.response && error.response.data && error.response.data.runid;
        if (runid) await this.openRun(runid); // วันนี้มีงานค้างอยู่แล้ว → เปิดงานนั้นให้ดู
        this.fail(error);
      }
    },
    contactEditable(row) {
      return this.awaiting && !this.confirming
        && (['NO_CONTACT', 'CONTACT_AMBIGUOUS'].includes(row.status) || ['manual', 'new'].includes(row.contact_source));
    },
    openPickContact(row) {
      clearTimeout(this.contactTimer);
      this.contactSeq += 1; // an answer for the previous row's search must not land here
      this.contactRow = row;
      this.contactQ = '';
      this.contactHits = [];
      this.contactSearching = false;
      this.pickDlg = true;
    },
    searchContacts() {
      clearTimeout(this.contactTimer);
      const q = this.contactQ.trim();
      if (q.length < 2) { this.contactSeq += 1; this.contactHits = []; this.contactSearching = false; return; }
      this.contactSearching = true;
      this.contactTimer = setTimeout(async () => {
        const seq = ++this.contactSeq;
        try {
          const r = await axios.post(this.baseURL + '/accountingContactSearch', { q }, this.auth());
          if (seq === this.contactSeq) this.contactHits = r.data.contacts || []; // a late answer never overwrites
        } catch (error) {
          if (seq === this.contactSeq) this.fail(error);
        } finally {
          if (seq === this.contactSeq) this.contactSearching = false;
        }
      }, 300);
    },
    openNewContact(row) {
      this.contactRow = row;
      // pre-filled only when the system is sure (one phone, from the app/LINE sender, nobody in Peak has it);
      // a phone reached through a name match must be typed by the manager, never just confirmed
      this.newPhone = row.new_contact_ok ? String(row.phones || '').split(',')[0] : '';
      this.newDlg = true;
    },
    lineConfirmable(row) {
      return this.awaiting && !this.confirming && row.status === 'NO_SLIP' && ['manual', 'new', 'name'].includes(row.contact_source)
        && Array.isArray(row.line_candidates) && row.line_candidates.length > 0;
    },
    // เปิดกล่องเมื่อรูปพร้อมแล้วเท่านั้น (มาตรฐาน dialog) — รูปที่เปิดไม่ได้แสดงเป็นข้อความ ไม่ทำให้กล่องค้าง
    async openLineSlip(row) {
      this.$emit('onLoading', true);
      const imgs = {};
      try {
        await Promise.all(row.line_candidates.map(async (c) => {
          try {
            const r = await axios.post(this.baseURL + '/accountingLineSlipImage', { itemid: row.itemid, message_id: c.message_id }, this.auth());
            await preloadImage(r.data.url);
            imgs[c.message_id] = r.data.url;
          } catch (error) {
            if (error.response && error.response.status === 401) throw error;
            imgs[c.message_id] = null;
          }
        }));
      } catch (error) {
        this.fail(error);
        return;
      } finally {
        this.$emit('onLoading', false);
      }
      this.lineRow = row;
      this.lineImgs = imgs;
      this.lineDlg = true;
    },
    // ดาวน์โหลดไฟล์สลิปไปแนบใบเสร็จใน Peak — ต้องผ่าน backend: bucket ไม่มี CORS เบราว์เซอร์ fetch presigned URL ข้ามโดเมนไม่ได้
    async downloadLineSlip(c) {
      const row = this.lineRow;
      if (!row || this.lineDownloading) return;
      this.lineDownloading = c.message_id;
      let href = null;
      try {
        const r = await axios.post(this.baseURL + '/accountingLineSlipDownload', { itemid: row.itemid, message_id: c.message_id },
          { ...this.auth(), responseType: 'blob' });
        href = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = href;
        const named = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '');
        a.download = named ? named[1] : `line-slip-${c.message_id}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (error) {
        // responseType blob: error.response.data is a Blob, so the server message cannot be read — one generic text
        this.$emit('onErrorHandler', this.$t('accountingSync.lineSlipDownloadFail'));
      } finally {
        if (href) setTimeout(() => URL.revokeObjectURL(href), 1000);
        this.lineDownloading = null;
      }
    },
    async confirmLineSlip(mid) {
      const row = this.lineRow;
      if (!row) return;
      try {
        await this.mutate('/accountingItemConfirmLineSlip', { itemid: row.itemid, message_id: mid });
        this.lineDlg = false;
        // ครั้งแรกไม่ติ๊กให้ — ผู้จัดการตรวจแล้วติ๊กเองก่อนสร้าง
        await this.openRun(this.selected.run.runid);
      } catch (error) {
        this.fail(error);
      }
    },
    pickContact(code) { return this.setContact('pick', code); },
    async setContact(action, code) {
      const row = this.contactRow;
      if (!row) return;
      try {
        const r = await this.mutate('/accountingItemContact', { itemid: row.itemid, action, code, phone: action === 'new' ? this.newPhone : undefined });
        this.pickDlg = false;
        this.newDlg = false;
        await this.openRun(this.selected.run.runid);
        const id = row.itemid;
        this.picked = r.data.status === 'CREATE' ? [...new Set([...this.picked, id])] : this.picked.filter((x) => x !== id);
      } catch (error) {
        this.fail(error);
      }
    },
    async loadFixes() {
      try {
        const r = await axios.post(this.baseURL + '/accountingProductFixes', {}, this.auth());
        this.fixes = r.data.fixes || [];
      } catch (error) {
        if (error.response && error.response.status === 401) this.fail(error);
      }
    },
    async deleteFix(row) {
      try {
        await this.mutate('/accountingProductFixDelete', { fixid: row.fixid });
        this.fixDeleting = null;
        await this.loadFixes();
      } catch (error) {
        this.fail(error);
      }
    },
    async openProduct(row, issue) {
      this.productRow = row;
      this.productIssue = issue;
      this.productQ = '';
      const f = issue.fixed;
      // การเลือกเก่า (ก่อนแบ่งได้) ไม่มี lines — สินค้าเดียวรับยอดทั้งแถว
      const chosen = f ? (f.lines && f.lines.length ? f.lines : [{ code: f.code, qty: f.qty, amount: issue.purchase }]) : [];
      this.productLines = chosen.map((ln) => ({ code: ln.code, qty: this.qty(ln.qty), amount: this.$moneyInput(ln.amount) }));
      this.productRemember = f ? !!f.remember : false;
      // เปิด dialog หลังโหลดรายการครบ (มาตรฐาน dialog) — ทะเบียนจาก Peak ณ ตอนทำแผนล่าสุด โหลดทั้งหมดแล้วกรองในจอ
      this.$emit('onLoading', true);
      try {
        const r = await axios.post(this.baseURL + '/accountingProductSearch', { q: '' }, this.auth());
        this.products = r.data.products || [];
      } catch (error) {
        this.fail(error);
        return;
      } finally {
        this.$emit('onLoading', false);
      }
      this.productDlg = true;
    },
    isPicked(code) { return this.productLines.some((ln) => ln.code === code); },
    productName(code) { const p = this.products.find((x) => x.code === code); return p ? p.name : code; },
    // แตะสินค้าในรายการ = เพิ่ม/เอาออก · สินค้าแรกรับยอดทั้งแถว ตัวที่เพิ่มทีหลังได้ยอดที่ยังเหลือ (กรอกแก้ได้)
    toggleProductLine(code) {
      if (this.isPicked(code)) {
        this.productLines = this.productLines.filter((ln) => ln.code !== code);
      } else {
        if (this.productLines.length === 1) this.productLines[0].amount = this.lineAmount(this.productLines[0]);
        const left = this.productLines.length ? this.productRemainCents : this.productPurchaseCents;
        this.productLines.push({ code, qty: '1', amount: left > 0 ? (left / 100).toFixed(2) : '' });
      }
      if (this.productSplit) this.productRemember = false; // แบ่งหลายสินค้าไม่จำ (เจ้าของเคาะ 4 พ.ย.)
    },
    // สินค้าเดียว = ยอดทั้งแถวเสมอ (ไม่มีช่องให้กรอก)
    lineAmount(ln) { return this.productSplit ? this.$moneyInput(ln.amount) : (this.productPurchaseCents / 100).toFixed(2); },
    lineCents(ln) {
      const v = this.lineAmount(ln);
      return /^\d+(\.\d{1,2})?$/.test(String(v)) ? Math.round(Number(v) * 100) : 0;
    },
    // ราคาต่อหน่วย = ยอด ÷ จำนวน เป็นสตางค์ล้วน (ต้องลงตัว) — สูตรเดียวกับ server
    lineUnit(ln) {
      const n = Number(ln.qty);
      const halves = Math.round(n * 2);
      const cents = this.lineCents(ln);
      if (!(n > 0) || halves !== n * 2 || !cents || (cents * 2) % halves) return null;
      return ((cents * 2) / halves / 100).toFixed(2);
    },
    // ราคาใน Peak (ถ้ามี) ต้องตรง — เจ้าของเคาะ: ไม่ตรง = เตือนแล้วไม่สร้าง
    linePeakDiff(ln) {
      const p = this.products.find((x) => x.code === ln.code);
      const unit = this.lineUnit(ln);
      return p && p.price != null && unit && Number(p.price).toFixed(2) !== unit ? p.price : null;
    },
    fixedText(f) {
      const lines = f.lines && f.lines.length ? f.lines : [f];
      return lines.map((ln) => `${ln.code} × ${this.qty(ln.qty)} @ ${this.$money(ln.price)}`).join(' + ');
    },
    async saveProduct() {
      try {
        await this.mutate('/accountingItemProduct', { itemid: this.productRow.itemid, row: this.productIssue.row,
          lines: this.productLines.map((ln) => ({ code: ln.code, qty: Number(ln.qty), amount: this.lineAmount(ln) })),
          remember: this.productRemember && !this.productSplit && this.productIssue.code === 'PRODUCT_UNMAPPED' });
        this.productDlg = false;
        await this.openRun(this.selected.run.runid);
        this.loadFixes();
      } catch (error) {
        this.fail(error);
        return;
      }
      // ระบุครบทุกแถวของงานนี้แล้ว = ทำแผนใหม่ให้เลย (เจ้าของ 4 พ.ย.: ไม่ต้องรอกดเอง)
      // ยังเหลือแถวอื่น = รอให้ครบก่อน ไม่งั้นทุกครั้งที่เลือกต้องรอแผนใหม่ ~2 นาทีกว่าจะเลือกแถวถัดไปได้
      // มีผู้ติดต่อที่เลือกเองในงานนี้ = ถามก่อน เพราะแผนใหม่ไม่พกผู้ติดต่อเหล่านั้นไปด้วย (ต้องเลือกใหม่)
      if (!this.awaiting || this.pendingProductCount) return;
      if (this.manualContactCount) this.replanAsk = true;
      else await this.replan();
    },
    // ยกเลิก + เริ่มงานวันเดิมในคำขอเดียว (server ยกเลิกเฉพาะงานที่ยังไม่มีใครกดยืนยัน) — แผนใหม่ใช้สินค้าที่ระบุไว้
    async replan() {
      const { runid } = this.selected.run;
      this.replanAsk = false;
      try {
        const r = await this.mutate('/accountingReplan', { runid });
        await this.loadRuns(true);
        await this.openRun(r.data.runid);
      } catch (error) {
        this.fail(error);
        await this.loadRuns(true);
        await this.openRun(runid, true);
      }
    },
    togglePick(id) {
      this.picked = this.picked.includes(id) ? this.picked.filter((x) => x !== id) : [...this.picked, id];
    },
    pickAll(on) {
      const manual = this.picked.filter((id) => !this.autoPickable.includes(id)); // ที่ติ๊กเองไว้ ไม่ถูกล้าง
      this.picked = on ? [...new Set([...this.autoPickable, ...manual])] : manual;
    },
    async confirmRun() {
      try {
        // ส่งสิ่งที่จอนี้เห็นไปด้วย — อีกแท็บอาจเปลี่ยนผู้ติดต่อ/โหมดไปแล้ว server จะตอบ 409 แทนการสร้างตามข้อมูลที่จอไม่เห็น
        const seen = this.selected.items.filter((i) => this.picked.includes(i.itemid))
          .map((i) => ({ itemid: i.itemid, peak_contact: i.peak_contact, peak_contact_name: i.peak_contact_name, contact_source: i.contact_source }));
        await this.mutate('/accountingConfirmRun', { runid: this.selected.run.runid, itemids: this.picked, seen, mode: this.confirmMode });
        this.confirming = false;
        await this.openRun(this.selected.run.runid);
        this.loadRuns(true);
      } catch (error) {
        this.fail(error);
        if (error.response && error.response.status === 409) { // ข้อมูลบนจอเก่า → โหลดใหม่ให้เห็นของจริงก่อนกดอีกครั้ง
          this.confirming = false;
          const before = new Map(this.selected.items.map((i) => [i.itemid, `${i.peak_contact}|${i.peak_contact_name}|${i.contact_source}`]));
          await this.loadRuns(true);
          await this.openRun(this.selected.run.runid, true);
          // แถวที่ผู้ติดต่อเปลี่ยนไปถูกเอาติ๊กออก — ต้องตรวจแล้วติ๊กเองใหม่ ไม่ใช่กดยืนยันซ้ำแล้วได้ผู้ติดต่อใหม่โดยไม่รู้ตัว
          const now = new Map(this.selected.items.map((i) => [i.itemid, `${i.peak_contact}|${i.peak_contact_name}|${i.contact_source}`]));
          this.picked = this.picked.filter((id) => now.has(id) && before.get(id) === now.get(id));
        }
      }
    },
    async cancelRun() {
      try {
        await this.mutate('/accountingCancelRun', { runid: this.selected.run.runid });
        await this.openRun(this.selected.run.runid);
        this.loadRuns(true);
      } catch (error) {
        this.fail(error);
      }
    },
    askMode(m) {
      // เปลี่ยนโหมดต้องยืนยันในแถบก่อน — ใบจริงแก้ใน Peak ไม่ได้ ทำได้แค่ยกเลิก
      this.pendingMode = m === this.mode ? null : m;
    },
    async saveMode(m) {
      try {
        const r = await this.mutate('/accountingSettings', { mode: m });
        this.mode = r.data.mode;
        this.pendingMode = null;
      } catch (error) {
        this.fail(error);
      }
    },
    downloadReport() {
      const blob = new Blob([this.selected.run.reportMd], { type: 'text/markdown;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `peak-${this.selected.run.bizdate}-run${this.selected.run.runid}.md`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000); // บางเบราว์เซอร์ยังอ่าน blob อยู่หลัง click
    },
    monthLabel(m) { return m ? this.$formatMonthYear(m + '-01') : ''; },
    reconActive(s) { return s === 'queued' || s === 'running'; },
    reconStatusLabel(s) { return this.$t('accountingSync.reconStatus.' + s); },
    async loadRecon(silent = false) {
      try {
        const r = await axios.post(this.baseURL + '/accountingReconRuns', {}, this.auth());
        this.reconRuns = r.data.runs || [];
      } catch (error) {
        if (!silent || (error.response && error.response.status === 401)) this.fail(error);
      } finally {
        this.loadingRecon = false;
      }
      this.scheduleRecon();
    },
    scheduleRecon() {
      // poll only while a job is queued/running (the worker takes a few minutes); never after leaving
      clearTimeout(this.reconTimer);
      if (this.alive && this.reconRuns.some((r) => this.reconActive(r.status))) {
        this.reconTimer = setTimeout(async () => {
          await this.loadRecon(true);
          if (this.recon && this.reconActive(this.recon.status)) this.openRecon(this.recon.reconid, true);
        }, DETAIL_POLL_MS);
      }
    },
    async openRecon(reconid, silent = false) {
      if (!silent) this.reconWant = reconid;
      try {
        const r = await axios.post(this.baseURL + '/accountingReconDetail', { reconid }, this.auth());
        if (this.reconWant !== reconid) return; // a later click asked for another job: drop this late answer
        const s = r.data.run.summary;
        // ผลจาก worker รุ่นอื่นอาจขาดบางช่อง — ห้ามให้ .length ของช่องที่ไม่มีทำทั้งหน้าพัง
        this.recon = { ...r.data.run, summary: s ? { ...s, bad: s.bad || [], explained: s.explained || [], notes: s.notes || [] } : null };
      } catch (error) {
        if (!silent || (error.response && error.response.status === 401)) this.fail(error);
      }
    },
    async startRecon() {
      try {
        const r = await this.mutate('/accountingReconStart', { month: this.reconMonth });
        await this.loadRecon(true);
        await this.openRecon(r.data.reconid);
      } catch (error) {
        const id = error.response && error.response.data && error.response.data.reconid;
        if (id) await this.openRecon(id); // this month already has a job running: show it
        this.fail(error);
      }
    },
    async cancelRecon() {
      try {
        await this.mutate('/accountingReconCancel', { reconid: this.recon.reconid });
        await this.loadRecon(true);
        await this.openRecon(this.recon.reconid);
      } catch (error) {
        this.fail(error);
      }
    },
    downloadRecon() {
      const blob = new Blob([this.recon.report_md], { type: 'text/markdown;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `reconcile-${this.recon.month}.md`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    },
    async loadCustomers(silent = false) {
      try {
        const r = await axios.post(this.baseURL + '/accountingCustomers', {}, this.auth());
        this.pending = r.data.pending || [];
        this.confirmedCount = r.data.confirmedCount || 0;
        const still = new Set(this.pending.map((p) => p.sheet_name));
        this.custPicked = this.custPicked.filter((n) => still.has(n));
      } catch (error) {
        if (!silent || (error.response && error.response.status === 401)) this.fail(error);
      }
    },
    toggleCust(name) {
      this.custPicked = this.custPicked.includes(name) ? this.custPicked.filter((x) => x !== name) : [...this.custPicked, name];
    },
    async decideCustomers() {
      const action = this.custAction;
      const names = action === 'confirm' ? this.custConfirmable : this.custPicked;
      try {
        await this.mutate('/accountingDecideCustomers', { action, names });
        this.custAction = null;
        this.custPicked = [];
        await this.loadCustomers();
        if (action === 'confirm' && this.runs.some((r) => r.status === 'awaiting_confirm')) {
          this.$emit('onInfoHandler', this.$t('accountingSync.custReplanHint'));
        }
      } catch (error) {
        this.fail(error);
      }
    },
  },
};
</script>

<style scoped>
.acc-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.acc-start { margin-bottom: 14px; }
.acc-date { flex: 0 0 220px; width: 220px; }
.acc-mode { flex: 0 0 220px; width: 220px; }
.acc-sec { margin: 18px 0 8px; display: flex; align-items: center; gap: 8px; }
.acc-tools { margin-bottom: 8px; }
.acc-proj { margin-left: 6px; }
.acc-note { margin-bottom: 8px; }
.acc-recon-totals { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 8px; margin: 8px 0; }
.acc-recon-total { padding: 10px 12px; border-radius: 10px; background: var(--c-surface-2, rgba(0, 0, 0, .04)); }
.acc-recon-line { display: grid; grid-template-columns: 16px 1fr auto; gap: 6px; padding: 2px 0; font-variant-numeric: tabular-nums; }
.acc-recon-sum { border-top: 1px solid var(--c-border, rgba(0, 0, 0, .15)); font-weight: 600; }
.acc-recon-op { text-align: center; }
.acc-recon-verdict { margin-top: 6px; font-weight: 600; }
.acc-good { color: var(--c-success); }
.acc-warn { color: var(--c-warning-tx); }
.acc-recon-list { margin: 10px 0; }
.acc-recon-head { font-weight: 600; margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
.acc-recon-head.acc-bad { color: var(--c-error); }
.acc-recon-head.acc-good { color: var(--c-success); margin: 10px 0; }
.acc-recon-day { padding: 6px 0; border-bottom: 1px solid var(--c-border, rgba(0, 0, 0, .08)); }
.acc-confirm { margin-top: 12px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.acc-confirm-bar { margin-top: 10px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; width: 100%;
  padding: 10px 12px; border-radius: 10px; background: var(--c-warning-bg); color: var(--c-warning-tx); }
.acc-contact-btns { display: inline-flex; gap: 6px; margin-left: 6px; }
.acc-line-slip { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid var(--c-border, rgba(0, 0, 0, .08)); }
.acc-line-imglink { display: block; flex: 0 1 420px; max-width: 100%; cursor: zoom-in; }
.acc-line-img { display: block; width: 100%; max-height: 70vh; object-fit: contain; border-radius: 8px; background: var(--c-surface-2, rgba(0, 0, 0, .04)); }
.acc-line-noimg { width: 180px; padding: 24px 8px; text-align: center; border-radius: 8px; background: var(--c-surface-2, rgba(0, 0, 0, .04)); }
.acc-line-meta { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
.acc-contact-list { display: flex; flex-direction: column; gap: 4px; max-height: 320px; overflow-y: auto; margin-top: 6px; }
.acc-contact-hit { display: flex; gap: 8px; text-align: left; padding: 8px 10px; border-radius: 8px; cursor: pointer;
  border: 1px solid var(--c-border, rgba(0, 0, 0, .1)); background: transparent; color: inherit; }
.acc-issue { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 4px; }
.acc-err { color: var(--c-error); }
/* dialog ระบุสินค้า */
.acc-pd-sum { display: flex; align-items: center; gap: 12px; padding: 12px 14px; margin-bottom: 12px; border-radius: 12px;
  background: var(--c-surface-2, rgba(0, 0, 0, .04)); }
.acc-pd-text { flex: 1; font-weight: 700; }
.acc-pd-amt { display: flex; flex-direction: column; align-items: flex-end; font-variant-numeric: tabular-nums; }
.acc-pd-amt b { font-size: 18px; }
.acc-pd-search { position: relative; }
.acc-pd-search .mdi { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--c-text-muted); pointer-events: none; }
.acc-pd-search .id-input { padding-left: 36px; }
.acc-pd-list { margin-top: 8px; max-height: 280px; overflow-y: auto; border: 1px solid var(--c-border); border-radius: 12px; }
.acc-pd-group { position: sticky; top: 0; z-index: 1; padding: 6px 12px; font-size: 12px; font-weight: 700; color: var(--c-text-muted);
  background: var(--c-surface-2); border-bottom: 1px solid var(--c-border); }
.acc-pd-row { display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 12px; text-align: left; cursor: pointer;
  border: 0; border-bottom: 1px solid var(--c-border); background: transparent; color: inherit; font: inherit; }
.acc-pd-row:last-child { border-bottom: 0; }
.acc-pd-row:hover { background: var(--c-surface-2, rgba(0, 0, 0, .04)); }
.acc-pd-row.on { background: var(--c-primary-soft); font-weight: 600; }
.acc-pd-radio { font-size: 18px; color: var(--c-text-muted); }
.acc-pd-row.on .acc-pd-radio { color: var(--c-primary); }
.acc-pd-name { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.acc-pd-code { font-size: 12px; color: var(--c-text-muted); }
.acc-pd-price { font-variant-numeric: tabular-nums; font-weight: 600; }
.acc-pd-empty { padding: 12px; }
.acc-pd-calc { display: flex; align-items: flex-end; gap: 14px; margin-top: 12px; flex-wrap: wrap; }
.acc-pd-qty { width: 180px; margin: 0; }
.acc-pd-unit { padding-bottom: 10px; font-variant-numeric: tabular-nums; }
.acc-pd-multi { margin: 8px 0 0; }
.acc-pd-line { margin-top: 12px; }
.acc-pd-line.split { padding: 10px 12px; border: 1px solid var(--c-border); border-radius: 12px; }
.acc-pd-line.split .acc-pd-calc { margin-top: 6px; }
.acc-pd-line-head { display: flex; align-items: center; gap: 10px; font-weight: 600; }
.acc-pd-remain { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 12px; padding: 9px 12px; border-radius: 12px;
  font-variant-numeric: tabular-nums; }
.acc-pd-remain.ok { background: var(--c-success-bg); color: var(--c-success-tx); }
.acc-pd-remain.bad { background: var(--c-warning-bg); color: var(--c-warning-tx); }
.acc-pd-remember { display: flex; align-items: flex-start; gap: 8px; margin-top: 12px; }
.acc-contact-hit:hover { background: var(--c-surface-2, rgba(0, 0, 0, .04)); }
.acc-error { margin: 8px 0; padding: 8px 12px; border-radius: 8px; background: var(--c-error-bg); color: var(--c-error-tx); }
</style>
